/**
 * EditorPane — Main content editor referencing editr-ui.
 *
 * Composes:
 * - editr-ui: MarkdownEditor, VersionHistorySidebar (when available)
 * - fastr-lib: Inline command palette for targeted AI actions (Cmd+K)
 * - reversr-ui: Edit history tracking (manual vs AI edits)
 * - widgetr-lib: Status bar elements
 *
 * This is the lightweight default editor. In full integration,
 * editr-ui's MarkdownEditor provides rich editing with:
 * - Syntax highlighting
 * - Live preview
 * - Toolbar with formatting commands
 * - Version history integration via backtrackr
 *
 * The textarea fallback matches the editr-ui interface so it can
 * be swapped in when the app has editr-ui in its dependency tree.
 */

import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useReversr } from '@opcentral/reversr-ui';
import { useExpressrContext } from '../context/ExpressrContext.js';
import { FastrCommandPalette, buildExpressrActions, type SelectionContext } from './FastrCommandPalette.js';
import { MarkdownPreview } from './MarkdownPreview.js';
import type { EditHistoryEntry } from './ReversrPanel.js';

export function EditorPane() {
  const { activeDocument, updateContent, saveDocument, synthesize, snapshots, config, state, setSidePanel, setViewMode } = useExpressrContext();
  const { recordChange, canUndo, canRedo } = useReversr();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [fastrOpen, setFastrOpen] = useState(false);
  const [selectionCtx, setSelectionCtx] = useState<SelectionContext | null>(null);
  const [generating, setGenerating] = useState(false);

  // Track content before edits for reversr history
  const contentBeforeEdit = useRef<string>('');
  const manualEditTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus textarea when document changes
  useEffect(() => {
    if (activeDocument && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [activeDocument?.id]);

  // Sync content ref when document changes
  useEffect(() => {
    if (activeDocument) {
      contentBeforeEdit.current = activeDocument.content;
    }
  }, [activeDocument?.id]);

  // Capture selection context when fastr opens
  const openFastr = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !activeDocument) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setSelectionCtx({
      selectedText: textarea.value.substring(start, end),
      fullContent: textarea.value,
      selectionStart: start,
      selectionEnd: end,
      documentTitle: activeDocument.title,
      format: activeDocument.format,
    });
    setFastrOpen(true);
  }, [activeDocument]);

  // Record an AI edit to reversr history
  const recordAIEdit = useCallback((before: string, after: string, description: string, action?: string) => {
    const entry: EditHistoryEntry = {
      before,
      after,
      source: 'ai',
      description,
      timestamp: Date.now(),
      action,
    };
    recordChange(entry);
    contentBeforeEdit.current = after;
  }, [recordChange]);

  // Record a manual edit (debounced — fires after user stops typing)
  const recordManualEdit = useCallback((newContent: string) => {
    if (manualEditTimer.current) {
      clearTimeout(manualEditTimer.current);
    }
    manualEditTimer.current = setTimeout(() => {
      const before = contentBeforeEdit.current;
      if (before !== newContent && before !== '') {
        const entry: EditHistoryEntry = {
          before,
          after: newContent,
          source: 'manual',
          description: 'Manual edit',
          timestamp: Date.now(),
        };
        recordChange(entry);
        contentBeforeEdit.current = newContent;
      }
    }, 1500); // 1.5s debounce — record after user pauses
  }, [recordChange]);

  // AI synthesis callback — used by fastr preset actions
  const handleSynthesize = useCallback(async (prompt: string, _selection: string): Promise<string> => {
    setGenerating(true);
    try {
      const result = await synthesize({
        topic: activeDocument?.title ?? 'content',
        sourceContent: [prompt],
        tone: activeDocument?.tone as any ?? 'professional',
        audience: (activeDocument?.audience ?? 'general') as any,
        format: activeDocument?.format ?? 'markdown',
        length: 'standard',
      });
      return result.content;
    } catch (err) {
      console.error('[expressr] Synthesis failed:', err);
      return '';
    } finally {
      setGenerating(false);
    }
  }, [synthesize, activeDocument]);

  // Replace selection in textarea + record AI edit
  const handleReplace = useCallback((text: string, actionDescription?: string) => {
    if (!selectionCtx || !activeDocument) return;
    const before = activeDocument.content;
    const newContent = activeDocument.content.substring(0, selectionCtx.selectionStart) + text + activeDocument.content.substring(selectionCtx.selectionEnd);
    updateContent(newContent);
    recordAIEdit(before, newContent, actionDescription ?? 'AI edit', actionDescription);
  }, [selectionCtx, activeDocument, updateContent, recordAIEdit]);

  // Insert text at cursor/end + record AI edit
  const handleInsert = useCallback((text: string, actionDescription?: string) => {
    if (!activeDocument) return;
    const before = activeDocument.content;
    const insertText = activeDocument.content ? `\n\n${text}` : text;
    const newContent = activeDocument.content + insertText;
    updateContent(newContent);
    recordAIEdit(before, newContent, actionDescription ?? 'AI insert', actionDescription);
  }, [activeDocument, updateContent, recordAIEdit]);

  // Custom prompt handler — targeted section editing with full document context
  const handleCustomPrompt = useCallback(async (prompt: string) => {
    if (!selectionCtx || !activeDocument) return;
    setGenerating(true);
    try {
      const hasSelection = selectionCtx.selectedText.length > 0;
      const systemPrompt = hasSelection
        ? `You are editing a ${activeDocument.format} document titled "${activeDocument.title}".

Full document for context:
---
${selectionCtx.fullContent}
---

The user has selected the following section to modify:
---
${selectionCtx.selectedText}
---

User instruction: ${prompt}

IMPORTANT: Return ONLY the modified version of the selected section. Do not include the rest of the document, explanations, or preamble. Output only the replacement text.`
        : `You are editing a ${activeDocument.format} document titled "${activeDocument.title}".

Full document for context:
---
${selectionCtx.fullContent}
---

User instruction: ${prompt}

Write new content to append to the document. Output only the new content.`;

      const result = await synthesize({
        topic: activeDocument.title,
        sourceContent: [systemPrompt],
        tone: activeDocument.tone as any ?? 'professional',
        audience: (activeDocument.audience ?? 'general') as any,
        format: activeDocument.format,
        length: 'standard',
      });

      if (result.content) {
        if (hasSelection) {
          handleReplace(result.content, `Custom: ${prompt}`);
        } else {
          handleInsert(result.content, `Custom: ${prompt}`);
        }
      }
    } catch (err) {
      console.error('[expressr] Custom prompt synthesis failed:', err);
    } finally {
      setGenerating(false);
    }
  }, [selectionCtx, activeDocument, synthesize, handleReplace, handleInsert]);

  // Handle textarea content change (manual edit)
  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    updateContent(newContent);
    recordManualEdit(newContent);
  }, [updateContent, recordManualEdit]);

  // Build fastr items — wrap onReplace/onInsert to pass action descriptions
  const fastrItems = useMemo(() => {
    if (!selectionCtx) return [];
    return buildExpressrActions(selectionCtx, {
      onSynthesize: handleSynthesize,
      onReplace: (text: string) => handleReplace(text),
      onInsert: (text: string) => handleInsert(text),
      onOpenSynthesis: () => setSidePanel('synthesis'),
    });
  }, [selectionCtx, handleSynthesize, handleReplace, handleInsert, setSidePanel]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Ctrl/Cmd+K = Fastr command palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        openFastr();
        return;
      }
      // Ctrl/Cmd+S = Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveDocument();
      }
      // Ctrl/Cmd+Z = Undo via reversr (when not native)
      // Note: native Ctrl+Z still works for textarea undo.
      // Ctrl/Cmd+Shift+S = Snapshot via snappr-lib
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        snapshots.createSnapshot(
          activeDocument?.id ?? '',
          'expressr',
          'Quick snapshot',
          {
            tool: 'expressr',
            command: 'quick-snapshot',
            author: config?.user?.name,
            errorsBeforeFix: 0,
            errorsAfterFix: 0,
            errorsFixed: 0,
            duration: 0,
            projectPath: '',
            branch: '',
          }
        ).catch(() => { /* snappr may not have git context */ });
      }
      // Tab = indent
      if (e.key === 'Tab') {
        e.preventDefault();
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const value = textarea.value;
        const newValue = value.substring(0, start) + '  ' + value.substring(end);
        updateContent(newValue);
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        });
      }
    },
    [openFastr, saveDocument, snapshots, activeDocument, config, updateContent]
  );

  if (!activeDocument) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>No Document Selected</div>
          <div style={{ fontSize: 12 }}>Create a new document or select one from the sidebar.</div>
          <div style={{ fontSize: 11, marginTop: 12 }}>Tip: Open the AI panel to generate content from scratch</div>
        </div>
      </div>
    );
  }

  const { viewMode, documentThemeId } = state;
  const showEditor = viewMode === 'edit' || viewMode === 'split';
  const showPreview = viewMode === 'preview' || viewMode === 'split';

  const viewModeBtn = (mode: 'edit' | 'preview' | 'split', label: string) => (
    <button
      onClick={() => setViewMode(mode)}
      style={{
        padding: '2px 8px',
        fontSize: 11,
        background: viewMode === mode ? 'rgba(108, 108, 240, 0.25)' : 'transparent',
        border: viewMode === mode ? '1px solid rgba(108, 108, 240, 0.4)' : '1px solid transparent',
        borderRadius: 3,
        color: viewMode === mode ? '#a0a0ff' : 'inherit',
        cursor: 'pointer',
        fontWeight: viewMode === mode ? 600 : 400,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Editor + Preview area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Textarea editor */}
        {showEditor && (
          <textarea
            ref={textareaRef}
            value={activeDocument.content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            placeholder={`Start writing your ${activeDocument.format} document...\n\nCtrl+S to save, Ctrl+Shift+S for a quick snapshot.\nCtrl+K for AI actions on selected text — type a custom instruction or pick a preset.`}
            style={{
              flex: 1,
              width: viewMode === 'split' ? '50%' : '100%',
              padding: '24px 32px',
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              fontSize: 14,
              lineHeight: 1.8,
              fontFamily: activeDocument.format === 'markdown'
                ? "'JetBrains Mono', 'Fira Code', monospace"
                : 'inherit',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
            }}
            spellCheck
          />
        )}

        {/* Split divider */}
        {viewMode === 'split' && (
          <div style={{ width: 1, flexShrink: 0, background: 'var(--border, #2a2a4a)' }} />
        )}

        {/* Markdown preview */}
        {showPreview && (
          <div style={{
            flex: 1,
            width: viewMode === 'split' ? '50%' : '100%',
            overflow: 'auto',
          }}>
            <MarkdownPreview
              content={activeDocument.content}
              themeId={documentThemeId}
              fillHeight
            />
          </div>
        )}
      </div>

      {/* Generation indicator */}
      {generating && (
        <div style={{
          position: 'absolute', top: 8, right: 16,
          background: '#2a2a4a', padding: '4px 10px', borderRadius: 4,
          fontSize: 12, opacity: 0.8,
        }}>
          Generating...
        </div>
      )}

      {/* Fastr command palette — with custom prompt support */}
      <FastrCommandPalette
        isOpen={fastrOpen}
        onClose={() => setFastrOpen(false)}
        items={fastrItems}
        placeholder={selectionCtx?.selectedText ? 'AI action or type a custom instruction...' : 'AI action...'}
        onCustomPrompt={handleCustomPrompt}
      />

      {/* Status bar — view mode toggle + document info */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px', borderTop: '1px solid var(--border-light, #1e1e38)', fontSize: 11, opacity: 0.6 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>{activeDocument.format.toUpperCase()}</span>
          <span>{activeDocument.wordCount} words</span>
          <span>~{Math.ceil(activeDocument.wordCount / 250)} min read</span>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {state.hasUnsavedChanges && <span style={{ marginRight: 8 }}>Modified</span>}
          {(canUndo || canRedo) && (
            <span style={{ opacity: 0.8, marginRight: 8 }}>
              {canUndo ? 'Undo' : ''}{canUndo && canRedo ? '/' : ''}{canRedo ? 'Redo' : ''} available
            </span>
          )}
          <span style={{ marginRight: 8 }}>⌘K AI</span>
          {viewModeBtn('edit', 'Edit')}
          {viewModeBtn('split', 'Split')}
          {viewModeBtn('preview', 'Preview')}
        </div>
      </div>
    </div>
  );
}
